import { format, subDays } from "date-fns"

export const REPORT_DATE_PRESET = {
  today: "today",
  yesterday: "yesterday",
  lastWeek: "last_week",
  last30Days: "last_30_days",
  custom: "custom",
  all: "all",
}

export const ACTIVITY_REPORT_URL_RANGE = {
  last30Days: "last_30_days",
}

function formatIsoDate(value) {
  return format(value, "yyyy-MM-dd")
}

export function buildReportDateRange(preset, customFrom = "", customTo = "") {
  const today = new Date()

  switch (preset) {
    case REPORT_DATE_PRESET.today: {
      const day = formatIsoDate(today)
      return { preset, from: day, to: day }
    }
    case REPORT_DATE_PRESET.yesterday: {
      const day = formatIsoDate(subDays(today, 1))
      return { preset, from: day, to: day }
    }
    case REPORT_DATE_PRESET.lastWeek:
      return {
        preset,
        from: formatIsoDate(subDays(today, 6)),
        to: formatIsoDate(today),
      }
    case REPORT_DATE_PRESET.last30Days:
      return {
        preset,
        from: formatIsoDate(subDays(today, 29)),
        to: formatIsoDate(today),
      }
    case REPORT_DATE_PRESET.all:
      return { preset, from: "", to: "" }
    case REPORT_DATE_PRESET.custom:
      return {
        preset,
        from: customFrom,
        to: customTo || customFrom,
      }
    default:
      return buildReportDateRange(REPORT_DATE_PRESET.last30Days)
  }
}

const URL_PRESETS = new Set([
  REPORT_DATE_PRESET.today,
  REPORT_DATE_PRESET.yesterday,
  REPORT_DATE_PRESET.lastWeek,
  REPORT_DATE_PRESET.last30Days,
  REPORT_DATE_PRESET.all,
])

/** URL params for a picked range: `range=<preset>`, plus from/to for custom. */
export function reportDateRangeToSearchParams(range) {
  const custom = range?.preset === REPORT_DATE_PRESET.custom
  return {
    range: range?.preset || null,
    from: custom ? range.from || null : null,
    to: custom ? range.to || null : null,
    report_date: null,
  }
}

export function parseActivityReportSearchParams(searchParams) {
  const rangeParam = searchParams.get("range")
  const reportDate = searchParams.get("report_date") || ""

  if (rangeParam === REPORT_DATE_PRESET.custom) {
    return {
      analyticsRange: buildReportDateRange(
        REPORT_DATE_PRESET.custom,
        searchParams.get("from") || "",
        searchParams.get("to") || ""
      ),
    }
  }

  if (URL_PRESETS.has(rangeParam)) {
    return { analyticsRange: buildReportDateRange(rangeParam) }
  }

  if (rangeParam === ACTIVITY_REPORT_URL_RANGE.last30Days) {
    const last30Days = buildReportDateRange(REPORT_DATE_PRESET.last30Days)
    return {
      analyticsRange: last30Days,
    }
  }

  if (reportDate) {
    const customRange = buildReportDateRange(
      REPORT_DATE_PRESET.custom,
      reportDate,
      reportDate
    )
    return {
      analyticsRange: customRange,
    }
  }

  return {
    analyticsRange: buildReportDateRange(REPORT_DATE_PRESET.last30Days),
  }
}

export function buildActivityReportEmailsLink({ rangePreset } = {}) {
  if (rangePreset === ACTIVITY_REPORT_URL_RANGE.last30Days) {
    return `/admin/activity-report-emails?range=${ACTIVITY_REPORT_URL_RANGE.last30Days}`
  }
  return "/admin/activity-report-emails"
}

export function formatReportRangeLabel(range, timezone) {
  if (!range?.from && !range?.to) {
    return "All dates"
  }
  if (range.from === range.to) {
    return `Report date · ${range.from}${
      timezone ? ` (${timezone.split("/").pop()?.replace("_", " ")})` : ""
    }`
  }
  if (range.preset === REPORT_DATE_PRESET.last30Days) {
    return `Last 30 days · ${range.from} to ${range.to}${
      timezone ? ` (${timezone.split("/").pop()?.replace("_", " ")})` : ""
    }`
  }
  return `Report dates · ${range.from} to ${range.to}${
    timezone ? ` (${timezone.split("/").pop()?.replace("_", " ")})` : ""
  }`
}

export function buildAnalyticsQueryParams(range) {
  if (!range?.from || !range?.to) {
    return {}
  }
  if (range.from === range.to) {
    return { report_date: range.from }
  }
  return {
    report_date_from: range.from,
    report_date_to: range.to,
  }
}

export function buildListingQueryParams(range) {
  if (!range?.from && !range?.to) {
    return {}
  }
  return {
    report_date_from: range.from || undefined,
    report_date_to: range.to || undefined,
  }
}
